import React from 'react';
import styled from '@emotion/styled';

import { Flex, Box } from '../Rebass';
import Heading from '../Heading';
import { GridContainer as Grid } from '../Container';
import { motion } from '../Motion';
import { useInView } from 'react-intersection-observer';
import { SxStyleProp } from '../Rebass/types';

interface ListProps {
  headline?: string;
  children?: React.ReactNode;
  renderActions?: () => React.ReactNode;
  renderDescription?: React.ReactNode;
  cols?: number;
  sx?: SxStyleProp;
  shouldAnimate?: boolean;
}

const StyledBox = styled(Box)`
  max-width: 1280px;
  margin: auto;
`;

const listVariants = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
  initial: {},
};

const listItemVariants = {
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 50,
      damping: 15,
    },
  },
  initial: {
    x: -20,
    opacity: 0,
  },
};

const AnimatedFlex = motion.custom(Flex);
const AnimatedGrid = motion.custom(Grid);
const AnimatedBox = motion.custom(styled(Box)`
  -ms-grid-column: ${({ index, cols }) => (index % cols) + 1};
  -ms-grid-row: ${({ index, cols }) => Math.floor(index / cols) + 1};
`);

const List = ({
  headline,
  children,
  cols,
  renderActions,
  renderDescription,
  sx,
  shouldAnimate = true,
}: ListProps) => {
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  const length = React.Children.count(children);
  const numRows = Math.ceil(length / cols);
  const { ref, inView } = useInView({ threshold: 0.2 });

  const animate = isMounted && shouldAnimate ? inView : !shouldAnimate;
  return (
    <StyledBox ref={ref} sx={sx} className="List">
      {headline && (
        <Flex
          mb={[4]}
          sx={{
            flexDirection: ['column', 'row'],
            justifyContent: [null, 'space-between'],
            alignItems: 'center',
          }}
        >
          <Heading size="h2" fontWeight={'normal'}>
            {headline}
          </Heading>
          {renderActions && renderActions()}
        </Flex>
      )}
      {renderDescription && <Box mb={[4]}>{renderDescription}</Box>}
      {cols ? (
        <AnimatedGrid
          sx={{
            gridTemplateColumns: ['1fr', '1fr', `repeat(${cols}, 1fr)`],
            columnGap: [2],
            gridTemplateRows: ['auto', 'auto', `repeat(${numRows}, 1fr)`],
            gridAutoFlow: ['row', 'row', 'column'],
          }}
          variants={listVariants}
          animate={animate ? 'animate' : 'initial'}
          initial="initial"
        >
          {React.Children.map(children, (child, index) => (
            <AnimatedBox index={index} cols={cols} variants={listItemVariants}>
              {child}
            </AnimatedBox>
          ))}
        </AnimatedGrid>
      ) : (
        <Box>
          <AnimatedFlex
            width="100%"
            flexWrap="wrap"
            variants={listVariants}
            animate={animate ? 'animate' : 'initial'}
            initial="initial"
          >
            {React.Children.map(children, (child) => (
              <AnimatedBox mr={[3]} variants={listItemVariants}>
                {child}
              </AnimatedBox>
            ))}
          </AnimatedFlex>
        </Box>
      )}
    </StyledBox>
  );
};
export default List;
